import React, { useCallback, useEffect, useState } from 'react';
import { useModal } from 'react-modal-hook';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router-dom';
import NavigationPrompt from 'react-router-navigation-prompt';
import dayjs, { Dayjs } from 'dayjs';
import { FieldArray, FormikProvider, useFormik } from 'formik';
import { Alert, Breadcrumbs, BreadcrumbsItem, PageHeader, PageHeaderContent, PageTitle } from 'components';
import { routeConstants, URL } from 'routes';
import { browserHistory } from 'routes/Router';
import { selectIsLoadingAPIRequest } from 'store/ApiClient';
import { selectCalculatorRules } from 'store/CalculatorRules';
import { selectCurrentEmployeeSite } from 'store/Employers';
import { Payment, payPaymentSetRequest } from 'store/Payment';
import { selectSiteAccountBalance, selectSiteSettings } from 'store/Site/selectors';
import { resetCalculatorPaymentSet, selectCalculatorChangeLog, selectCalculatorPaymentSet, selectCalculatorResults, updateCalculatorPaymentset } from 'store/TipCalculator';
import { IStore } from 'store/types';
import { ICalculatorPaymentSegment, PayoutMethodTypes, PayoutRefType } from 'types';
import { buildPaymentSetCalculations, centsToDollarsDisplay } from 'utils';
import { IntegrationDataProcessingModal } from './components/modals/IntegrationDataProcessingModal';
import { Checks, DateSelector, LaborData, Pools, Review, SharedPOS, TipData } from './steps';
import ChangeLog from './steps/ChangeLog';
import { CalculatorSteps, ICalculatorPaymentForm } from './types';

interface IParams {
  step: CalculatorSteps;
}

const makePaymentRoutes: string[] = [
  URL.MakePaymentCalculator,
  URL.MakePaymentCalculatorPools,
  URL.MakePaymentCalculatorSharedPOS,
  URL.MakePaymentCalculatorChecks,
  URL.MakePaymentCalculatorLaborData,
  URL.MakePaymentCalculatorTipAmounts,
  URL.MakePaymentCalculatorReview,
  URL.MakePaymentCalculatorChangeLog,
  `${URL.SettingsCalculator}/tip-shares`,
];

const PayCalculatorTips = () => {
  const location = useLocation();
  const { step } = useParams<IParams>();
  const dispatch = useDispatch();

  // Site State
  const currentSite = useSelector(selectCurrentEmployeeSite);
  const currentSiteId = currentSite?.id;
  const siteBalance = useSelector(selectSiteAccountBalance());

  const { timezone: currentSiteTimezone, holdback: currentSiteHoldbacks, tipCalculatorPercentFee: currentSiteTipCalcPercentFee } = useSelector(selectSiteSettings()) || {};

  // TipCalculator State
  const tipCalcResults = useSelector(selectCalculatorResults());
  const tipCalcPaymentSet = useSelector(selectCalculatorPaymentSet());
  const paymentSetSegments = tipCalcPaymentSet?.segments;
  const currentCalculatorRules = useSelector(selectCalculatorRules());
  const changeLog = useSelector(selectCalculatorChangeLog());

  // Component State
  const [sideBarExpanded, setSideBarExpanded] = useState<boolean>(false);
  const isProcessing = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, Payment.CREATE_PAYMENT_REQUEST));

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  useEffect(() => {
    if (!currentCalculatorRules) browserHistory.push(routeConstants.private.makePayment);
    dispatch(resetCalculatorPaymentSet());
  }, []);

  const [showIntegrationDataProcessingModal, hideIntegrationDataProcessingModal] = useModal(
    () => (
      <IntegrationDataProcessingModal
        tipCalcRules={currentCalculatorRules}
        onClose={hideIntegrationDataProcessingModal}
        businessDate={tipCalcPaymentSet.date}
        siteId={currentSiteId}
        siteHoldbackSettings={currentSiteHoldbacks}
        segments={paymentSetSegments}
        paymentSet={tipCalcPaymentSet}
        creditCardPercentFee={currentSiteTipCalcPercentFee}
        timeZone={currentSiteTimezone}
      />
    ),
    [currentCalculatorRules, currentSiteHoldbacks, paymentSetSegments, tipCalcPaymentSet]
  );

  const onDateSelect = useCallback(
    (date: Dayjs, daySegments: ICalculatorPaymentSegment[]) => {
      // update payment set & show processing modal
      dispatch(updateCalculatorPaymentset({ ...tipCalcPaymentSet, date: dayjs(date).format('YYYY-MM-DD'), siteId: currentSiteId, segments: daySegments }));
      showIntegrationDataProcessingModal();
    },
    [tipCalcPaymentSet, currentSiteId]
  );

  const formik = useFormik({
    initialValues: {
      payees: [],
      description: '',
      authorizedAgreement: false,
      paymentDate: tipCalcPaymentSet?.date ? dayjs(tipCalcPaymentSet.date) : undefined,
    } as ICalculatorPaymentForm,
    onSubmit: ({ payees, description, paymentDate, paymentCategory }) => {
      dispatch(
        payPaymentSetRequest({
          calculations: buildPaymentSetCalculations(currentCalculatorRules.method, tipCalcResults, currentSiteTipCalcPercentFee),
          changeLog,
          description: description || '',
          paymentDate: paymentDate.format('YYYY-MM-DD'),
          paymentCategory,
          refId: paymentDate.format('YYYY-MM-DD'),
          refType: PayoutRefType.CALCULATOR,
          payouts: payees.map(({ employee, note, gross, autoGratuity, holdbackAmount = 0 }) => ({
            currency: 'usd',
            employeeId: employee.id,
            gross,
            ...(autoGratuity && { autoGratuity }),
            holdback: holdbackAmount,
            job: employee.job,
            net: gross - holdbackAmount,
            notes: note || '',
            type: 'tip',
          })),
          siteId: currentSiteId,
          workflow: PayoutMethodTypes.CALCULATOR,
        })
      );
    },
    validateOnBlur: true,
    enableReinitialize: true,
    validateOnChange: false,
  });

  const { paymentDate: formDate } = formik.values;

  const pageHeaderContent = (
    <PageHeader data-cy="make-payment-header">
      <Breadcrumbs data-cy="payments-breadcrumbs">
        <BreadcrumbsItem data-cy="payments-breadcrumblink" label="Payments" to={URL.PaymentsOverview} />
        <BreadcrumbsItem data-cy="payments-breadcrumblink" label="Make Payment" to={URL.MakePayment} />
        <BreadcrumbsItem data-cy="make-payments-breadcrumblink" label="Calculator" />
      </Breadcrumbs>

      <PageHeaderContent>
        <PageTitle title={currentSite?.name} subtitle={`Account Balance: ${siteBalance || siteBalance === 0 ? centsToDollarsDisplay(siteBalance) : '--'}`} />
      </PageHeaderContent>
    </PageHeader>
  );
  const goToReview = () => browserHistory.push(routeConstants.private.makePaymentCalculatorReview);

  if (!currentSite || !currentCalculatorRules) return null;

  return (
    <FormikProvider value={formik}>
      <FieldArray
        name="payees"
        render={() => (
          <>
            <NavigationPrompt
              when={(crntLocation, nextLocation) => {
                // when redirected after logout or changing organization
                if (nextLocation?.pathname === URL.Login || nextLocation?.pathname === URL.RootPrivate) return false;

                // when redirected after a successful submission
                if (nextLocation && nextLocation.pathname === URL.MakePaymentSummary) return false;

                return (!nextLocation || !makePaymentRoutes.includes(nextLocation.pathname)) && !!formDate;
              }}
            >
              {({ onConfirm, onCancel }) => (
                <Alert
                  data-cy="leave-payment-alert"
                  title="Leave make payment?"
                  message="Leaving this page will abandon all work. Are you sure you want to leave?"
                  onLeftAction={onCancel}
                  onRightAction={() => {
                    dispatch(resetCalculatorPaymentSet());
                    onConfirm();
                  }}
                  leftActionLabel="Cancel"
                  rightActionLabel="Leave"
                  isActive
                />
              )}
            </NavigationPrompt>

            {step === CalculatorSteps.SELECT_DATE && <DateSelector currentSite={currentSite} currentSiteId={currentSiteId} pageHeaderContent={pageHeaderContent} onNext={onDateSelect} />}

            {step === CalculatorSteps.REVIEW && <Review formik={formik} isProcessing={isProcessing} onExpand={() => setSideBarExpanded(!sideBarExpanded)} pageHeaderContent={pageHeaderContent} sidebarOpen={sideBarExpanded} />}

            {step === CalculatorSteps.POOLS && <Pools pageHeaderContent={pageHeaderContent} onExpand={() => setSideBarExpanded(!sideBarExpanded)} sidebarOpen={sideBarExpanded} onBack={goToReview} />}

            {step === CalculatorSteps.SHARED_POS && (
              <SharedPOS currentCalculatorRules={currentCalculatorRules} pageHeaderContent={pageHeaderContent} onExpand={() => setSideBarExpanded(!sideBarExpanded)} sidebarOpen={sideBarExpanded} onBack={goToReview} />
            )}

            {step === CalculatorSteps.CHECKS && (
              <Checks currentCalculatorRules={currentCalculatorRules} currentSite={currentSite} pageHeaderContent={pageHeaderContent} onExpand={() => setSideBarExpanded(!sideBarExpanded)} sidebarOpen={sideBarExpanded} onBack={goToReview} />
            )}

            {step === CalculatorSteps.LABOR_DATA && (
              <LaborData
                currentCalculatorRules={currentCalculatorRules}
                currentSite={currentSite}
                pageHeaderContent={pageHeaderContent}
                onExpand={() => setSideBarExpanded(!sideBarExpanded)}
                sidebarOpen={sideBarExpanded}
                onBack={goToReview}
              />
            )}

            {step === CalculatorSteps.TIP_DATA && (
              <TipData
                currentCalculatorRules={currentCalculatorRules}
                currentSite={currentSite}
                pageHeaderContent={pageHeaderContent}
                onExpand={() => setSideBarExpanded(!sideBarExpanded)}
                sidebarOpen={sideBarExpanded}
                onBack={goToReview}
              />
            )}

            {step === CalculatorSteps.CHANGE_LOG && <ChangeLog pageHeaderContent={pageHeaderContent} onExpand={() => setSideBarExpanded(!sideBarExpanded)} sidebarOpen={sideBarExpanded} onBack={goToReview} />}
          </>
        )}
      />
    </FormikProvider>
  );
};
export default PayCalculatorTips;
